/**
 * The Trust Centre EXTERNAL PORTAL — a PUBLIC page for a party with no account.
 *
 * ISOLATION, ON THE CLIENT SIDE TOO:
 *   * NO APP SHELL, NO TENANT NAVIGATION, NO TENANT SIGN-IN. This renders with
 *     no tenant session and must never acquire one.
 *   * IT USES `fetch` DIRECTLY, NEVER the `api` client. The `api` client attaches
 *     the TENANT bearer token and, on a 401, tries to refresh and tear down a
 *     tenant session. A portal visitor has no tenant session; their credential
 *     is the PORTAL token minted at `/api/trust-portal/login`, which lives only
 *     in this component's memory and is sent only to `/api/trust-portal/*`.
 *   * The portal token is NEVER written to localStorage: a reload signs in
 *     again. A read-only portal does not need to persist a credential, and not
 *     persisting it keeps any XSS from making the session durable.
 *
 * The page is reachable at `/trust/:slug/portal`, routed before the tenant app
 * in App.tsx exactly like the public trust page and the vendor questionnaire.
 */

import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardHeader, Button, Field, Pill, Spinner, ErrorNotice, type Tone } from '../components/ui';
import { IconShield, IconKey } from '../components/icons';
import { useTrustPortalT } from '../lib/trustPortalStrings';
import { TrustCentreView, type PublicTrustView } from './TrustCentre';

type Tier = 'PUBLIC' | 'NDA' | 'NAMED';

interface Profile {
  email: string;
  name: string | null;
  company: string | null;
  organizationName: string;
  role: 'CLIENT' | 'PARTNER' | 'AUDITOR';
  ndaAccepted: boolean;
  ndaWouldUnlock: boolean;
  expiresAt: string | null;
  visibleTiers: Tier[];
}

interface PortalAnswer {
  id: string;
  question: string;
  answer: string;
  category: string | null;
  tier: Tier;
  updatedAt: string;
}

const TIER_TONE: Record<Tier, Tone> = { PUBLIC: 'neutral', NDA: 'warning', NAMED: 'serious' };

/** A portal-scoped fetch. The token is passed explicitly — never ambient. */
async function portalFetch<T>(path: string, token: string | null, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (init?.body) headers['Content-Type'] = 'application/json';
  if (token) headers.Authorization = `Bearer ${token}`;
  const response = await fetch(`/api/trust-portal${path}`, {
    ...init,
    headers: { ...headers, ...(init?.headers as Record<string, string> | undefined) },
    credentials: 'omit', // never send tenant cookies to the portal
    cache: 'no-store',
    referrerPolicy: 'no-referrer',
  });
  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as
      | { error?: { code?: string } }
      | null;
    const err = new Error(payload?.error?.code ?? `HTTP_${response.status}`) as Error & {
      status: number;
      code: string;
    };
    err.status = response.status;
    err.code = payload?.error?.code ?? `HTTP_${response.status}`;
    throw err;
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export function TrustPortalPage() {
  const { slug = '' } = useParams<{ slug: string }>();
  const { tx, formatDate } = useTrustPortalT();

  const [token, setToken] = useState<string | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [answers, setAnswers] = useState<PortalAnswer[]>([]);
  const [documents, setDocuments] = useState<PublicTrustView | null>(null);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [signingIn, setSigningIn] = useState(false);
  const [loginError, setLoginError] = useState(false);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    document.title = tx('trust.portal.title');
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => meta.remove();
  }, [tx]);

  const loadAll = useCallback(async (tkn: string) => {
    setLoadError(false);
    try {
      const [me, ans, docs] = await Promise.all([
        portalFetch<Profile>('/me', tkn),
        portalFetch<{ visibleTiers: Tier[]; answers: PortalAnswer[] }>('/answers', tkn),
        portalFetch<{ published: boolean; view: PublicTrustView | null }>('/documents', tkn),
      ]);
      setProfile(me);
      setAnswers(ans.answers);
      setDocuments(docs.view);
    } catch {
      setLoadError(true);
    }
  }, []);

  const signIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setSigningIn(true);
    setLoginError(false);
    try {
      const result = await portalFetch<{ token: string; profile: Profile }>('/login', null, {
        method: 'POST',
        body: JSON.stringify({ slug, email: email.trim().toLowerCase(), password }),
      });
      setToken(result.token);
      setPassword('');
      await loadAll(result.token);
    } catch {
      setLoginError(true);
    } finally {
      setSigningIn(false);
    }
  };

  const acceptNda = async () => {
    if (!token) return;
    try {
      await portalFetch('/nda/accept', token, { method: 'POST', body: JSON.stringify({ accepted: true }) });
      await loadAll(token);
    } catch {
      setLoadError(true);
    }
  };

  const signOut = () => {
    setToken(null);
    setProfile(null);
    setAnswers([]);
    setDocuments(null);
    setEmail('');
    setPassword('');
  };

  // ---- Signed out: the login card ------------------------------------------
  if (!token || !profile) {
    return (
      <div className="min-h-dvh bg-surface-sunken grid place-items-center px-4 py-10">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2 mb-4 justify-center">
            <span className="grid place-items-center w-11 h-11 rounded-xl bg-brand/10 text-brand">
              <IconShield className="w-6 h-6" />
            </span>
          </div>
          <Card>
            <CardHeader title={tx('trust.portal.title')} />
            <form onSubmit={signIn} className="space-y-3">
              <Field label={tx('trust.portal.email')} htmlFor="portal-email">
                <input id="portal-email" type="email" autoComplete="username" className="oc-input"
                  value={email} onChange={(e) => setEmail(e.target.value)} required />
              </Field>
              <Field label={tx('trust.portal.password')} htmlFor="portal-password">
                <input id="portal-password" type="password" autoComplete="current-password" className="oc-input"
                  value={password} onChange={(e) => setPassword(e.target.value)} required />
              </Field>
              {loginError && <p className="text-sm text-critical" role="alert">{tx('trust.portal.signInFailed')}</p>}
              <Button type="submit" variant="primary" disabled={signingIn} className="w-full">
                {signingIn ? tx('trust.portal.signingIn') : tx('trust.portal.signIn')}
              </Button>
            </form>
          </Card>
        </div>
      </div>
    );
  }

  // ---- Signed in -----------------------------------------------------------
  const canSeeMoreWithNda = !profile.ndaAccepted && profile.ndaWouldUnlock;

  return (
    <div className="min-h-dvh bg-surface-sunken">
      <main className="mx-auto w-full max-w-3xl px-4 sm:px-6 py-6 sm:py-10 space-y-4">
        <header className="oc-card p-5 sm:p-6">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0">
              <span className="shrink-0 grid place-items-center w-11 h-11 rounded-xl bg-brand/10 text-brand">
                <IconShield className="w-6 h-6" />
              </span>
              <div className="min-w-0">
                <p className="text-xs font-medium uppercase tracking-wide text-ink-muted">{tx('trust.portal.title')}</p>
                <h1 className="oc-h1 mt-0.5 break-words">{profile.organizationName}</h1>
                <p className="oc-muted mt-1">{tx('trust.portal.welcome', { name: profile.organizationName })}</p>
              </div>
            </div>
            <Button size="sm" variant="ghost" onClick={signOut}>{tx('trust.portal.signOut')}</Button>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {profile.visibleTiers.map((t) => (
              <Pill key={t} tone={TIER_TONE[t]}>{tx(`trust.tier.${t}` as 'trust.tier.PUBLIC')}</Pill>
            ))}
            {profile.expiresAt && (
              <span className="text-xs text-ink-muted">{tx('trust.portal.expiresOn', { date: formatDate(profile.expiresAt) })}</span>
            )}
          </div>
        </header>

        {loadError && <ErrorNotice message={tx('trust.portal.loadFailed')} onRetry={() => void loadAll(token)} />}

        {/* NDA clickwrap gate — shown only when accepting it would reveal more. */}
        {canSeeMoreWithNda && (
          <Card>
            <CardHeader
              title={<span className="inline-flex items-center gap-2"><IconKey className="w-5 h-5 text-ink-muted" />{tx('trust.portal.ndaTitle')}</span>}
            />
            <p className="text-ink-secondary">{tx('trust.portal.ndaBody')}</p>
            <p className="mt-2 text-sm text-warning">{tx('trust.portal.ndaUnlocks')}</p>
            <div className="mt-3">
              <Button variant="primary" onClick={() => void acceptNda()}>{tx('trust.portal.ndaAccept')}</Button>
            </div>
          </Card>
        )}

        {/* The gated reverse-replies. */}
        <Card>
          <CardHeader title={tx('trust.portal.answersTitle')} subtitle={tx('trust.portal.readOnly')} />
          {answers.length === 0 ? (
            <p className="text-sm text-ink-muted">{tx('trust.portal.answersEmpty')}</p>
          ) : (
            <ul className="space-y-3">
              {answers.map((a) => (
                <li key={a.id} className="rounded-xl border border-line p-4">
                  <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1.5">
                    <p className="font-medium text-ink break-words min-w-0">{a.question}</p>
                    <Pill tone={TIER_TONE[a.tier]}>{tx(`trust.tier.${a.tier}` as 'trust.tier.PUBLIC')}</Pill>
                  </div>
                  {a.category && <p className="text-xs text-ink-muted mt-0.5">{a.category}</p>}
                  <p className="mt-2 text-ink-secondary whitespace-pre-line break-words">{a.answer}</p>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {/* The published posture, reusing the public page's presentational view. */}
        {documents && (
          <div>
            <h2 className="oc-h2 mb-2">{tx('trust.portal.documentsTitle')}</h2>
            <TrustCentreView view={documents} />
          </div>
        )}
      </main>
    </div>
  );
}
